import React, { FC, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { ProjectId, TaskId, TaskIdV2 } from 'api/models';
import { Loading } from 'components/atoms/loading';
import { TaskTable } from 'components/molecules/tasks/task-table';
import { toastService } from 'components/molecules/toast';
import { useTaskDrawer } from 'components/organisms/task-drawer';
import { Task } from 'data/tasks';
import {
  createMultipleSubTasksSagaAction,
  UpdateTaskActionPayloadOptions,
  updateTaskSagaAction,
} from 'data/tasks/actions';
import { useTasksByIdsFromRQ } from 'data/tasks/hooks';
import { TaskDrawerTab } from 'hooks/use-navigate-to-task';
import { useTranslation } from 'hooks/use-translation';
import { useUpdateSubTaskOrderMutation } from 'queries';
import { invalidateTaskQuery } from 'utils/invalidate-task-query';

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 130px;
`;

interface Props {
  taskId: number;
  projectId: number;
  data: number[];
  isExpanded: boolean;
  onDueDateChange: (taskId, [start, due]) => void;
  datePickerLoader?: boolean;
  entityId: number;
  options?: UpdateTaskActionPayloadOptions;
}

export const SubTasksList: FC<Props> = (props) => {
  const {
    data = [],
    taskId,
    projectId,
    isExpanded,
    onDueDateChange,
    datePickerLoader,
    entityId,
    options,
  } = props;
  const dispatch = useDispatch();
  const { tasks, isLoading } = useTasksByIdsFromRQ(ProjectId(projectId), data);
  const { mutate } = useUpdateSubTaskOrderMutation(taskId);
  const queryClient = useQueryClient();

  const handleAddMultiple = (subtasks: Array<string>) => {
    dispatch(
      createMultipleSubTasksSagaAction({
        projectId,
        subtasks: subtasks.map((subtask) => ({ taskName: subtask })),
        parentTask: taskId,
        queryClient,
      }),
    );

    return Promise.resolve();
  };

  const translatedPlaceholderText = useTranslation('placeholder.sub_task.new');
  const [_, showTaskDrawer] = useTaskDrawer();

  const handleTaskNameClick = useCallback(
    ([, _projectId, _taskId]: TaskId) => {
      showTaskDrawer(
        ProjectId(_projectId),
        TaskIdV2(_taskId),
        TaskDrawerTab.Conversations,
      );
    },
    [showTaskDrawer],
  );
  const title = useTranslation('task.reorder.success.title');
  const message = useTranslation('task.reorder.success.message');
  const handleSort = useCallback(
    (sorted: Task[]) => {
      mutate(
        { subTaskOrder: sorted.map((t) => t.taskId) },
        {
          onSuccess: async () => {
            toastService({ title, message }).success();
            dispatch(
              updateTaskSagaAction({
                projectId,
                taskId,
                data: {
                  subTaskOrder: sorted.map((t) => t.taskId),
                },
              }),
            );
            invalidateTaskQuery(taskId);
          },
        },
      );
    },
    [message, mutate, projectId, taskId, title, dispatch],
  );

  if (isLoading) {
    return (
      <LoadingWrapper>
        <Loading />
      </LoadingWrapper>
    );
  }

  return (
    <TaskTable
      onSort={handleSort}
      hideEmptyStateImage
      onTaskNameClick={handleTaskNameClick}
      projectId={projectId}
      data={tasks}
      newRowPlaceholder={translatedPlaceholderText}
      handleMultipleTasksAdded={handleAddMultiple}
      isSubTask
      autoFocus={false}
      isExpanded={isExpanded}
      canAddFormAsSubTask
      taskId={taskId}
      options={options}
      onDateChange={onDueDateChange}
      datePickerLoader={datePickerLoader}
      entityId={entityId}
    />
  );
};
